/**
 * Provenance for data this app reads from ANOTHER system (CNV-004).
 *
 * Scoped honestly, per the backlog row: the client search is a live Dataverse query, and the G/L map, history and
 * uploads are this app's own Cosmos records read live, so a "read just now" line on them would tell nobody anything
 * they could act on. The one surface whose truth can be stale is the Xero row: ptc-core serves it from its own SQL,
 * and the COMPANY row it resolves through is a nightly mirror of Dataverse. A client added since the last refresh
 * therefore reads "Not linked" when nobody knows, so that row says how old those records are.
 */

/** One missed nightly refresh, plus margin. Past this, a "Not linked" can be the mirror's lag, not the truth. */
export const CLIENT_RECORDS_OVERDUE_AFTER_HOURS = 36;

export type ClientRecordsFreshness = 'fresh' | 'overdue' | 'unknown';

/** `unknown` (never `fresh`) when there is no readable time: an absent refresh time must not read as "all good". */
export function clientRecordsFreshness(refreshedAtUtc: string | null | undefined, now: Date): ClientRecordsFreshness {
  if (!refreshedAtUtc) return 'unknown';
  const at = new Date(refreshedAtUtc).getTime();
  if (Number.isNaN(at)) return 'unknown';
  const ageHours = (now.getTime() - at) / 3_600_000;
  return ageHours > CLIENT_RECORDS_OVERDUE_AFTER_HOURS ? 'overdue' : 'fresh';
}

/**
 * A moment in the VIEWER's zone and language. `conversionHistory.formatDateTime` prints UTC, which is right for a
 * file's period but wrong for "when was this refreshed": a 22:16Z refresh is 6:16 PM to staff in Florida.
 */
export function formatLocalDateTime(iso: string, locale: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(iso));
}
